import { ItemInstance, PersonalEquipment, PersonalSlot, PERSONAL_SLOTS, ItemBonuses, ConsumableInstance } from '../types/Item.types';
import { ITEM_DEFINITIONS, CONSUMABLE_DEFINITIONS } from '../data/items';
import { ConsumableEffect } from '../types/Item.types';
import { CombatStats } from '../types/Combat.types';
import { UidFactory } from './UidFactory';

// === GESTIONNAIRE D'INVENTAIRE (SOURCE DE VÉRITÉ UNIQUE) ===
// Gère tout l'inventaire de l'équipe pendant un run :
// - Équipement personnel par personnage (5 slots chacun)
// - Sac à dos partagé (1 item, donne la capacité de transport)
// - Transport partagé (items dans le sac, capacité limitée)
// - Consommables partagés (max 3)

const BASE_CAPACITY = 5;
const MAX_CONSUMABLES = 3;

/** Re-export pour rétro-compatibilité */
export type { ConsumableInstance };

export class InventoryManager {
  /** Équipement individuel par personnage (5 slots personnels) */
  private equipments: Map<string, PersonalEquipment> = new Map();
  /** Sac à dos partagé par toute l'équipe (donne les slots de transport) */
  private backpack: ItemInstance | null = null;
  /** Items en transport dans le sac (partagés par l'équipe) */
  private transport: ItemInstance[] = [];
  /** Pool de consommables partagé (max 3) */
  private consumables: ConsumableInstance[] = [];

  // =============================
  // ÉQUIPEMENT PAR PERSONNAGE
  // =============================

  /** Retourne l'équipement personnel d'un membre (créé à la demande) */
  getEquipment(memberId: string): PersonalEquipment {
    if (!this.equipments.has(memberId)) {
      this.equipments.set(memberId, {
        weapon: null, armor: null, belt: null, ring: null, boots: null,
      });
    }
    return this.equipments.get(memberId)!;
  }

  /** Retourne tous les équipements (pour itération/sérialisation) */
  getAllEquipments(): Map<string, PersonalEquipment> {
    return this.equipments;
  }

  /** Équipe un item sur un personnage. Retourne l'ancien item du slot (ou null). */
  equipItemOnCharacter(item: ItemInstance, memberId: string, slot: PersonalSlot): ItemInstance | null {
    const equip = this.getEquipment(memberId);
    const old = equip[slot];
    equip[slot] = item;
    return old;
  }

  /** Déséquipe un slot d'un personnage. Retourne l'item retiré (ou null). */
  unequipFromCharacter(memberId: string, slot: PersonalSlot): ItemInstance | null {
    const equip = this.getEquipment(memberId);
    const item = equip[slot];
    equip[slot] = null;
    return item;
  }

  // =============================
  // SAC À DOS PARTAGÉ
  // =============================

  /** Retourne le sac à dos équipé (ou null) */
  getBackpack(): ItemInstance | null {
    return this.backpack;
  }

  /** Équipe un sac à dos. Retourne l'ancien sac (ou null). */
  equipBackpack(item: ItemInstance): ItemInstance | null {
    const old = this.backpack;
    this.backpack = item;
    return old;
  }

  /** Déséquipe le sac à dos. Retourne le sac retiré (ou null). */
  unequipBackpack(): ItemInstance | null {
    const item = this.backpack;
    this.backpack = null;
    return item;
  }

  // =============================
  // TRANSPORT (items dans le sac)
  // =============================

  /** Capacité max du transport (base 5 + bonus sac à dos) */
  getTransportCapacity(): number {
    if (!this.backpack) return BASE_CAPACITY;
    const def = ITEM_DEFINITIONS[this.backpack.definitionId];
    return BASE_CAPACITY + (def?.bonuses?.capacity ?? 0);
  }

  /** Nombre d'items actuellement dans le transport */
  getTransportCount(): number {
    return this.transport.length;
  }

  /** Vérifie s'il reste de la place dans le transport */
  hasTransportSpace(): boolean {
    return this.transport.length < this.getTransportCapacity();
  }

  /** Retourne les items en transport (lecture seule) */
  getTransport(): ItemInstance[] {
    return this.transport;
  }

  /** Ajoute un item au transport. Retourne false si plein. */
  addToTransport(item: ItemInstance): boolean {
    if (!this.hasTransportSpace()) return false;
    this.transport.push(item);
    return true;
  }

  /** Retire un item du transport par UID. Retourne l'item ou null. */
  removeFromTransport(uid: string): ItemInstance | null {
    const idx = this.transport.findIndex(i => i.uid === uid);
    if (idx === -1) return null;
    return this.transport.splice(idx, 1)[0];
  }

  // =============================
  // CONSOMMABLES (pool partagé)
  // =============================

  /** Retourne les consommables de l'équipe */
  getConsumables(): ConsumableInstance[] {
    return this.consumables;
  }

  /** Ajoute un consommable. Retourne null si déjà 3 consommables. */
  addConsumable(definitionId: string): ConsumableInstance | null {
    if (this.consumables.length >= MAX_CONSUMABLES) return null;
    const c: ConsumableInstance = { uid: UidFactory.next('cons'), definitionId };
    this.consumables.push(c);
    return c;
  }

  /** Utilise un consommable. Retourne son effet ou null. */
  useConsumable(uid: string): ConsumableEffect | null {
    const idx = this.consumables.findIndex(c => c.uid === uid);
    if (idx === -1) return null;
    const c = this.consumables.splice(idx, 1)[0];
    const def = CONSUMABLE_DEFINITIONS[c.definitionId];
    return def?.effect ?? null;
  }

  /** Jette un consommable sans appliquer son effet. Retourne true si trouvé/supprimé. */
  discardConsumable(uid: string): boolean {
    const idx = this.consumables.findIndex(c => c.uid === uid);
    if (idx === -1) return false;
    this.consumables.splice(idx, 1);
    return true;
  }

  /** Nombre max de consommables */
  getMaxConsumables(): number {
    return MAX_CONSUMABLES;
  }

  // =============================
  // STATS DE COMBAT
  // =============================

  /** Calcule les stats finales d'un personnage (base + équipement + bonus sac) */
  computeStats(memberId: string, baseStats: CombatStats): CombatStats {
    const bonuses = this.getTotalBonuses(memberId);
    return {
      hp: baseStats.hp + (bonuses.maxHp ?? 0),
      maxHp: baseStats.maxHp + (bonuses.maxHp ?? 0),
      attack: baseStats.attack + (bonuses.attack ?? 0),
      defense: baseStats.defense + (bonuses.defense ?? 0),
      pa: baseStats.pa + (bonuses.pa ?? 0),
      pm: Math.max(1, baseStats.pm + (bonuses.pm ?? 0)),
    };
  }

  /** Portée bonus de l'arme d'un personnage */
  getBonusRange(memberId: string): number {
    const bonuses = this.getTotalBonuses(memberId);
    return bonuses.range ?? 0;
  }

  /** Somme des bonus : équipement personnel + sac à dos partagé */
  private getTotalBonuses(memberId: string): Partial<ItemBonuses> {
    const total: Partial<ItemBonuses> = {};

    // Bonus de l'équipement personnel (5 slots)
    const equip = this.getEquipment(memberId);
    for (const slot of PERSONAL_SLOTS) {
      const item = equip[slot];
      if (!item) continue;
      const def = ITEM_DEFINITIONS[item.definitionId];
      if (!def) continue;
      for (const [key, value] of Object.entries(def.bonuses)) {
        const k = key as keyof ItemBonuses;
        total[k] = (total[k] ?? 0) + (value as number);
      }
    }

    // Bonus du sac à dos partagé (s'applique à toute l'équipe, sauf capacity)
    if (this.backpack) {
      const bagDef = ITEM_DEFINITIONS[this.backpack.definitionId];
      if (bagDef) {
        for (const [key, value] of Object.entries(bagDef.bonuses)) {
          if (key === 'capacity') continue; // capacity = slots de transport, pas un bonus de combat
          const k = key as keyof ItemBonuses;
          total[k] = (total[k] ?? 0) + (value as number);
        }
      }
    }

    return total;
  }

  // =============================
  // LOOT & CRÉATION D'ITEMS
  // =============================

  /** Récupère un item de loot → va dans le transport. Retourne null si sac plein. */
  pickLootItem(definitionId: string): ItemInstance | null {
    if (!this.hasTransportSpace()) return null;
    const item = this.createItem(definitionId);
    this.transport.push(item);
    return item;
  }

  /** Crée un item avec un UID unique (via UidFactory partagée). */
  createItem(definitionId: string): ItemInstance {
    return { uid: UidFactory.next('item'), definitionId };
  }

  // =============================
  // GESTION DES UIDs (legacy compat)
  // =============================

  /** Itère tous les UIDs d'items/consommables stockés dans cet inventaire. */
  private *iterAllUids(): Generator<string> {
    for (const equip of this.equipments.values()) {
      for (const slot of PERSONAL_SLOTS) {
        const item = equip[slot];
        if (item) yield item.uid;
      }
    }
    if (this.backpack) yield this.backpack.uid;
    for (const item of this.transport) yield item.uid;
    for (const c of this.consumables) yield c.uid;
  }

  /**
   * Signale à la UidFactory tous les UIDs déjà présents — appelé après
   * déserialisation pour garantir que les prochaines générations ne
   * collisionneront pas avec les UIDs legacy.
   */
  fixNextUid(): void {
    UidFactory.observeAll(this.iterAllUids());
  }

  // =============================
  // SÉRIALISATION
  // =============================

  /** Sérialise l'inventaire complet pour sauvegarde */
  serialize(): object {
    const equipData: Record<string, Record<string, { uid: string; definitionId: string } | null>> = {};
    for (const [memberId, equip] of this.equipments) {
      equipData[memberId] = {};
      for (const slot of PERSONAL_SLOTS) {
        const item = equip[slot];
        equipData[memberId][slot] = item ? { uid: item.uid, definitionId: item.definitionId } : null;
      }
    }

    return {
      equipments: equipData,
      backpack: this.backpack ? { uid: this.backpack.uid, definitionId: this.backpack.definitionId } : null,
      transport: this.transport.map(i => ({ uid: i.uid, definitionId: i.definitionId })),
      consumables: this.consumables.map(c => ({ uid: c.uid, definitionId: c.definitionId })),
      // nextUid retiré : la source de vérité est désormais UidFactory (localStorage partagé)
    };
  }

  /** Restaure un inventaire depuis des données sérialisées */
  static deserialize(data: any): InventoryManager {
    const mgr = new InventoryManager();

    // Restaurer les équipements personnels
    if (data.equipments) {
      for (const [memberId, slots] of Object.entries(data.equipments as Record<string, any>)) {
        const equip = mgr.getEquipment(memberId);
        for (const slot of PERSONAL_SLOTS) {
          if (slots[slot]) {
            equip[slot] = { uid: slots[slot].uid, definitionId: slots[slot].definitionId };
          }
        }
      }
    }

    // Restaurer le sac à dos partagé
    if (data.backpack) {
      mgr.backpack = { uid: data.backpack.uid, definitionId: data.backpack.definitionId };
    }

    // Restaurer le transport
    if (Array.isArray(data.transport)) {
      for (const item of data.transport) {
        mgr.transport.push({ uid: item.uid, definitionId: item.definitionId });
      }
    }

    // Restaurer les consommables
    if (Array.isArray(data.consumables)) {
      for (const c of data.consumables) {
        mgr.consumables.push({ uid: c.uid, definitionId: c.definitionId });
      }
    }

    // Signaler à la UidFactory tous les UIDs restaurés pour qu'elle avance son compteur global.
    // Gère aussi les sauvegardes legacy qui avaient leur propre `nextUid` en stockant
    // des UIDs comme `item_42` — observeAll retient la valeur max.
    mgr.fixNextUid();
    // Compat legacy : observer aussi l'ancien nextUid s'il existait
    if (typeof data.nextUid === 'number' && data.nextUid > 0) {
      UidFactory.observe(`item_${data.nextUid - 1}`);
    }
    return mgr;
  }
}
